import React, { useState, useEffect, useCallback } from 'react';
import stockReorderApi, { errorMessage } from '../../../api/stockReorderApi';

const EMPTY_BRANCH = { company_id: '', branch: '' };

const FILE_ACCEPT = '.xls,.xlsx,.xlsm,.csv,.htm,.html';

/**
 * Company master and branches.
 *
 * Each branch holds its own data files, uploaded here and kept by the service.
 * Uploading again replaces the branch's current file. Reorder runs use the
 * stored file unless someone uploads a one-off on the Reorder Sheet tab.
 *
 * Uploads live on the rows rather than the add/edit form because a file has to
 * belong to a branch that already exists.
 */
const BranchesTab = ({ onChanged }) => {
    const [companies, setCompanies] = useState([]);
    const [units, setUnits] = useState([]);
    const [newCompany, setNewCompany] = useState('');
    const [form, setForm] = useState(EMPTY_BRANCH);
    const [editingId, setEditingId] = useState(null);
    const [uploadingKey, setUploadingKey] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setBusy(true);
        setError('');
        try {
            const [c, b] = await Promise.all([
                stockReorderApi.listCompanies(),
                stockReorderApi.listAllBusinessUnits()
            ]);
            setCompanies(c.companies || []);
            setUnits(b.business_units || []);
        } catch (err) {
            setError(errorMessage(err, 'Could not load companies and branches'));
        } finally {
            setBusy(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const act = async (fn, successReset) => {
        setBusy(true);
        setError('');
        try {
            await fn();
            if (successReset) successReset();
            await load();
            if (onChanged) await onChanged();
        } catch (err) {
            setError(errorMessage(err, 'That did not work'));
        } finally {
            setBusy(false);
        }
    };

    const addCompany = () =>
        act(() => stockReorderApi.createCompany(newCompany.trim()), () => setNewCompany(''));

    const removeCompany = (id) => {
        if (!window.confirm('Delete this company?')) return;
        act(() => stockReorderApi.deleteCompany(id));
    };

    const submitBranch = () => {
        const body = { ...form, branch: form.branch.trim() };
        act(
            () =>
                editingId
                    ? stockReorderApi.updateBusinessUnit(editingId, body)
                    : stockReorderApi.createBusinessUnit(body),
            () => {
                setForm(EMPTY_BRANCH);
                setEditingId(null);
            }
        );
    };

    const editBranch = (bu) => {
        setEditingId(bu.id);
        setForm({ company_id: bu.company_id, branch: bu.branch });
    };

    const uploadFile = async (buId, kind, event) => {
        const file = event.target.files?.[0];
        // Clear it straight away, otherwise picking the same file twice in a row
        // fires no change event and the upload silently does nothing.
        event.target.value = '';
        if (!file) return;

        setUploadingKey(`${buId}:${kind}`);
        setError('');
        try {
            await stockReorderApi.uploadBranchFile(buId, kind, file);
            await load();
            onChanged?.();
        } catch (err) {
            setError(errorMessage(err, 'Could not upload that file'));
        } finally {
            setUploadingKey(null);
        }
    };

    const removeFile = (buId, kind) => {
        if (!window.confirm(`Remove the stored ${kind} file for this branch?`)) return;
        act(() => stockReorderApi.deleteBranchFile(buId, kind));
    };

    const removeBranch = (id) => {
        if (!window.confirm('Delete this branch? Its saved sheets and supplier overrides go too.'))
            return;
        act(() => stockReorderApi.deleteBusinessUnit(id));
    };

    const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    /** One table cell: what is stored, and the controls to replace or clear it. */
    const fileCell = (bu, kind) => {
        const meta = bu[`${kind}_file`];
        const uploading = uploadingKey === `${bu.id}:${kind}`;

        return (
            <div className="sr-file-cell">
                {meta ? (
                    <div>
                        <div className="sr-path">{meta.filename}</div>
                        <span className="sr-muted">
                            {meta.uploaded_at ? new Date(meta.uploaded_at).toLocaleString() : ''}
                        </span>
                    </div>
                ) : kind === 'stock' ? (
                    <span className="sr-tag sr-tag-warn">not uploaded</span>
                ) : (
                    <span className="sr-muted">—</span>
                )}

                <div className="sr-nowrap">
                    <label className="sr-btn sr-btn-sm">
                        {uploading ? 'Uploading…' : meta ? 'Replace' : 'Upload'}
                        <input
                            type="file"
                            accept={FILE_ACCEPT}
                            hidden
                            disabled={busy || uploading}
                            onChange={(e) => uploadFile(bu.id, kind, e)}
                        />
                    </label>
                    {meta && (
                        <button
                            className="sr-btn sr-btn-sm sr-btn-danger"
                            onClick={() => removeFile(bu.id, kind)}
                            disabled={busy || uploading}
                        >
                            Remove
                        </button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="sr-stack">
            {error && <div className="sr-alert sr-alert-error">{error}</div>}

            <section className="sr-card">
                <h2 className="sr-card-title">Companies</h2>
                <div className="sr-inline-form">
                    <input
                        type="text"
                        placeholder="New company name"
                        value={newCompany}
                        onChange={(e) => setNewCompany(e.target.value)}
                    />
                    <button
                        className="sr-btn sr-btn-primary"
                        onClick={addCompany}
                        disabled={busy || !newCompany.trim()}
                    >
                        Add company
                    </button>
                </div>
                <div className="sr-table-wrap">
                    <table className="sr-table">
                        <thead>
                            <tr>
                                <th>Company</th>
                                <th className="sr-right">Branches</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {companies.map((c) => {
                                const count = units.filter((u) => u.company_id === c.id).length;
                                return (
                                    <tr key={c.id}>
                                        <td>{c.name}</td>
                                        <td className="sr-right">{count}</td>
                                        <td className="sr-right">
                                            <button
                                                className="sr-btn sr-btn-sm sr-btn-danger"
                                                onClick={() => removeCompany(c.id)}
                                                disabled={busy || count > 0}
                                                title={
                                                    count > 0
                                                        ? 'Delete its branches first'
                                                        : 'Delete this company'
                                                }
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {companies.length === 0 && (
                                <tr>
                                    <td colSpan={3} className="sr-empty">
                                        No companies yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="sr-card">
                <h2 className="sr-card-title">{editingId ? 'Edit branch' : 'Add a branch'}</h2>
                <div className="sr-grid-2">
                    <div className="sr-field">
                        <label htmlFor="sr-branch-company">Company</label>
                        <select
                            id="sr-branch-company"
                            value={form.company_id}
                            onChange={setField('company_id')}
                        >
                            <option value="">Select…</option>
                            {companies.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="sr-field">
                        <label htmlFor="sr-branch-name">Branch name</label>
                        <input
                            id="sr-branch-name"
                            type="text"
                            value={form.branch}
                            onChange={setField('branch')}
                        />
                    </div>
                </div>
                <p className="sr-muted">
                    Add the branch first, then upload its data files from the table below.
                </p>
                <div className="sr-actions">
                    <button
                        className="sr-btn sr-btn-primary"
                        onClick={submitBranch}
                        disabled={busy || !form.company_id || !form.branch.trim()}
                    >
                        {editingId ? 'Save changes' : 'Add branch'}
                    </button>
                    {editingId && (
                        <button
                            className="sr-btn"
                            onClick={() => {
                                setEditingId(null);
                                setForm(EMPTY_BRANCH);
                            }}
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </section>

            <section className="sr-card">
                <h2 className="sr-card-title">Branches</h2>
                <div className="sr-table-wrap">
                    <table className="sr-table">
                        <thead>
                            <tr>
                                <th>Company</th>
                                <th>Branch</th>
                                <th>Stock file</th>
                                <th>Supplier file</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {units.map((bu) => (
                                <tr key={bu.id}>
                                    <td>{bu.company}</td>
                                    <td>{bu.branch}</td>
                                    <td>{fileCell(bu, 'stock')}</td>
                                    <td>{fileCell(bu, 'supplier')}</td>
                                    <td className="sr-right sr-nowrap">
                                        <button
                                            className="sr-btn sr-btn-sm"
                                            onClick={() => editBranch(bu)}
                                            disabled={busy}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className="sr-btn sr-btn-sm sr-btn-danger"
                                            onClick={() => removeBranch(bu.id)}
                                            disabled={busy}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {units.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="sr-empty">
                                        No branches yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
};

export default BranchesTab;
